import { useCallback, useEffect } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UITile from 'UIComponents/tile/UITile';
import UITileSection from 'UIComponents/tile/UITileSection';
import UIButton from 'UIComponents/button/UIButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { useOpenCopilot } from 'copilot-toolkit/hooks/useOpenCopilot';
import AILoadingButton from 'ai-components-ui-library/components/buttons/AILoadingButton';
import { useInlineFeedback } from 'ai-components-ui-library/components/feedback/hooks/useInlineFeedback';
import CopilotPreviewInner from './internal/CopilotPreviewInner';
import { useLatestPreview } from '../../hooks/useLatestPreview';
import { useStreamingAction } from '../../hooks/useStreamingAction';

// Taken from BE here https://git.hubteam.com/HubSpot/chatspot-backend/blob/master/chatspot-core/src/main/java/com/hubspot/chatspot/core/api/previews/PreviewSource.java#L8
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CopilotPreview = ({
  appName,
  children,
  copilotAction,
  copilotActionButtonText,
  numHoursTilSummaryStale = 24,
  previewTitle,
  previewKey,
  previewSource,
  showCloseButton = true,
  usageTracker,
  useActionButton = true
}) => {
  const openCopilot = useOpenCopilot();
  const {
    sendAction,
    actionResponse,
    actionLoading,
    onReset
  } = useStreamingAction();
  const {
    latestPreview,
    latestPreviewLoading,
    latestPreviewError,
    onResetPreview
  } = useLatestPreview({
    previewSource,
    previewKey
  });
  const {
    onPositiveFeedback,
    onNegativeFeedback
  } = useInlineFeedback();
  const sourceHostAppPathname = window.location.pathname;
  const handleSummarize = useCallback(() => {
    sendAction({
      data: {
        action: copilotAction,
        chatPreview: {
          previewSource,
          previewKey
        },
        sourceHostApp: appName,
        sourceHostAppPathname,
        source: 'ACTION'
      }
    });
  }, [appName, copilotAction, sendAction, sourceHostAppPathname, previewSource, previewKey]);
  const handleReset = useCallback(() => {
    onResetPreview();
    onReset();
  }, [onResetPreview, onReset]);
  const handleRefresh = useCallback(() => {
    handleReset();
    sendAction({
      data: {
        action: copilotAction,
        chatPreview: {
          previewSource,
          previewKey
        },
        sourceHostApp: appName,
        sourceHostAppPathname,
        source: 'ACTION'
      },
      isRefresh: true
    });
  }, [appName, copilotAction, handleReset, sendAction, sourceHostAppPathname, previewSource, previewKey]);
  const handleCopilot = useCallback(() => {
    openCopilot();
  }, [openCopilot]);
  useEffect(() => {
    if (!useActionButton && latestPreviewError && latestPreviewError.status === 404) {
      /**
       * When not using the action button we will try and load the latest preview
       * If the API throws a 404 we will fire the action to get a new preview
       */
      handleSummarize();
    }
  }, [handleSummarize, useActionButton, latestPreviewError]);
  if (useActionButton && !actionResponse && !latestPreview) {
    return /*#__PURE__*/_jsx(AILoadingButton, {
      featureCategoryId: "123",
      loading: latestPreviewLoading || actionLoading,
      onClick: handleSummarize,
      size: "xs",
      usageTracker: usageTracker,
      use: "tertiary-light",
      children: copilotActionButtonText || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "aiAddonCopilotPreview.components.copilotPreview.buttonTitle"
      })
    });
  }
  return /*#__PURE__*/_jsx(UITile, {
    children: /*#__PURE__*/_jsxs(UITileSection, {
      style: {
        padding: '16px'
      },
      children: [/*#__PURE__*/_jsx(CopilotPreviewInner, {
        previewTitle: previewTitle,
        numHoursTilSummaryStale: numHoursTilSummaryStale,
        onRefresh: handleRefresh,
        onPositiveFeedback: onPositiveFeedback,
        onNegativeFeedback: onNegativeFeedback,
        response: latestPreview || actionResponse,
        loading: latestPreviewLoading || actionLoading,
        usageTracker: usageTracker,
        children: children
      }), /*#__PURE__*/_jsxs("div", {
        className: "m-top-4",
        children: [useActionButton && showCloseButton && /*#__PURE__*/_jsx(UIButton, {
          onClick: handleReset,
          size: "xs",
          use: "tertiary-light",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "aiAddonCopilotPreview.components.copilotPreview.closeButton"
          })
        }), /*#__PURE__*/_jsx(UITooltip, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "aiAddonCopilotPreview.components.copilotPreview.chatWithChatSpotButton.tooltip"
          }),
          children: /*#__PURE__*/_jsx(UIButton, {
            onClick: handleCopilot,
            size: "xs",
            use: "tertiary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "aiAddonCopilotPreview.components.copilotPreview.chatWithChatSpotButton.message"
            })
          })
        })]
      })]
    })
  });
};
export default CopilotPreview;